import { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Table, Badge } from "react-bootstrap";
import { NavLink, Route, Routes } from "react-router-dom";
import { fetchProfiles } from "../lib/profileApi";
import "./Admin.css";

const navItems = [
  { to: "/admin", label: "Overview", end: true },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/activity", label: "Activity" },
  { to: "/admin/reports", label: "Reports" },
  { to: "/admin/settings", label: "Settings" },
];

function Sidebar() {
  return (
    <aside className="admin-sidebar">
      <div className="admin-brand">Moonlightvale Admin</div>
      <nav className="admin-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `admin-link${isActive ? " active" : ""}`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function Topbar({ totalProfiles }) {
  return (
    <div className="admin-topbar">
      <h5 className="mb-0">Social System Admin</h5>
      <div className="admin-topbar-badge">{totalProfiles} profiles tracked</div>
    </div>
  );
}

function Dashboard({ profiles }) {
  const profilesWithImages = profiles.filter((profile) => profile.image).length;
  const locations = new Set(profiles.map((profile) => profile.location).filter(Boolean)).size;
  const services = new Set(profiles.map((profile) => profile.service).filter(Boolean)).size;

  return (
    <div>
      <h3 className="admin-section-title mb-2">System Overview</h3>
      <p className="admin-section-subtitle admin-muted mb-4">
        A quick summary of users currently registered in the platform.
      </p>
      <Row className="g-4">
        <Col md={6} xl={3}>
          <Card className="admin-card" body>
            <div className="admin-card-label">Total Profiles</div>
            <div className="admin-card-value">{profiles.length}</div>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="admin-card" body>
            <div className="admin-card-label">Profiles With Images</div>
            <div className="admin-card-value">{profilesWithImages}</div>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="admin-card" body>
            <div className="admin-card-label">Active Locations</div>
            <div className="admin-card-value">{locations}</div>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="admin-card" body>
            <div className="admin-card-label">Service Types</div>
            <div className="admin-card-value">{services}</div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

function Users({ profiles }) {
  return (
    <div>
      <h3 className="admin-section-title mb-4">Users</h3>
      <div className="admin-panel">
        <Table striped bordered hover responsive variant="dark" className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Service</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {profiles.length > 0 ? (
              profiles.map((profile) => (
                <tr key={profile.id}>
                  <td>{profile.name || "Unknown"}</td>
                  <td>{profile.contact || "Not set"}</td>
                  <td>{profile.location || "Not set"}</td>
                  <td>{profile.service || "Not set"}</td>
                  <td>
                    <Badge bg="success">Active</Badge>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center admin-empty">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function Activity({ profiles }) {
  const recentProfiles = [...profiles]
    .sort((left, right) => {
      const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
      return rightTime - leftTime;
    })
    .slice(0, 8);

  return (
    <div>
      <h3 className="admin-section-title mb-4">Activity</h3>
      <div className="admin-panel">
        <Table striped bordered hover responsive variant="dark" className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Created</th>
              <th>Image</th>
            </tr>
          </thead>
          <tbody>
            {recentProfiles.length > 0 ? (
              recentProfiles.map((profile) => (
                <tr key={profile.id}>
                  <td>{profile.id}</td>
                  <td>{profile.name || "Unknown"}</td>
                  <td>
                    {profile.createdAt
                      ? new Date(profile.createdAt).toLocaleString()
                      : "Unknown"}
                  </td>
                  <td>{profile.image ? "Uploaded" : "Missing"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center admin-empty">
                  No recent activity yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function Reports({ profiles }) {
  const serviceCounts = profiles.reduce((counts, profile) => {
    const key = profile.service || "Uncategorized";
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  return (
    <div>
      <h3 className="admin-section-title mb-4">Reports</h3>
      <div className="admin-panel">
        {Object.keys(serviceCounts).length > 0 ? (
          <Table striped bordered hover responsive variant="dark" className="admin-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Total Profiles</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(serviceCounts).map(([service, total]) => (
                <tr key={service}>
                  <td>{service}</td>
                  <td>{total}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="admin-empty">No report data available yet.</div>
        )}
      </div>
    </div>
  );
}

function Settings() {
  return (
    <div>
      <h3 className="admin-section-title mb-4">Settings</h3>
      <div className="admin-panel">
        <p className="admin-muted mb-2">Admin tools placeholder</p>
        <p className="mb-0">
          This section is ready for platform settings like moderation rules, admin roles,
          and system preferences.
        </p>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        const result = await fetchProfiles();
        setProfiles(result);
      } catch (error) {
        setErrorMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfiles();
  }, []);

  const content = useMemo(() => {
    if (isLoading) {
      return <div className="admin-empty">Loading admin dashboard...</div>;
    }

    if (errorMessage) {
      return <div className="admin-empty">{errorMessage}</div>;
    }

    return (
      <Routes>
        <Route index element={<Dashboard profiles={profiles} />} />
        <Route path="users" element={<Users profiles={profiles} />} />
        <Route path="activity" element={<Activity profiles={profiles} />} />
        <Route path="reports" element={<Reports profiles={profiles} />} />
        <Route path="settings" element={<Settings />} />
      </Routes>
    );
  }, [errorMessage, isLoading, profiles]);

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <Sidebar />
        <div className="admin-main">
          <Topbar totalProfiles={profiles.length} />
          <Container fluid className="admin-content">
            {content}
          </Container>
        </div>
      </div>
    </div>
  );
}
